/**
 * Returns / RMA route bundle — both the plant-context customer return
 * views and the global staff RMA workflow (issue, quick-ship, process,
 * invoice, result summary). Gated behind the per-tenant EXCLUDE_RETURNS
 * build flag so the area and its lazy chunks drop out of a tenant build
 * that does not license returns.
 *
 * Contains:
 * - returnsRoutes — array of <Route> elements, or null when excluded
 */
import React from 'react';
import { Route } from 'react-router-dom';

import { determineCanViewRMAs, determineIsStaff } from '@sophocles/router/checkCredentials';
import {
  rmaCreatePath,
  rmaInvoicePath,
  rmaIssuePath,
  rmaListPath,
  rmaProcessPath,
  rmaQuickShipPath,
  rmaBatchPrintPath,
  rmaResultSummaryPath,
  rmaViewPath,
  siteGroupRmaListPath,
  siteGroupRmaViewPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';

// --- Build-time DCE block ---
// Gated on the bare DefinePlugin global so webpack can prune the lazy()
// import() calls (and the resulting chunks) at build time when the flag
// is true.  Never extract the global into a variable — that would defeat
// tree-shaking.

export let returnsRoutes = null;
if (!__EXCLUDE_RETURNS__) {
  const ReturnPrintView = React.lazy(() => import('@sophocles/views/returns/ReturnPrintView'));
  const CustomerReturnGridView = React.lazy(() => import('@sophocles/views/returns/CustomerReturnGridView'));
  const InternalReturnGridView = React.lazy(() => import('@sophocles/views/returns/InternalReturnGridView'));
  const ReturnView = React.lazy(() => import('@sophocles/views/returns/ReturnView'));
  const CustomerReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.CustomerReturnView })),
  );
  const EditReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.EditReturnView })),
  );
  const InvoiceReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.InvoiceReturnView })),
  );
  const NewReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.NewReturnView })),
  );
  const ProcessReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.ProcessReturnView })),
  );
  const QuickShipReturnView = React.lazy(() =>
    import('@sophocles/views/returns/ReturnView').then(m => ({ default: m.QuickShipReturnView })),
  );

  returnsRoutes = [
    // Plant-context RMA routes
    <Route
      key={siteGroupRmaListPath.pattern}
      path={siteGroupRmaListPath.pattern}
      element={<RequireAuth component={CustomerReturnGridView} onEnter={determineCanViewRMAs} />}
    />,
    <Route
      key={siteGroupRmaViewPath.pattern}
      path={siteGroupRmaViewPath.pattern}
      element={<RequireAuth component={CustomerReturnView} onEnter={determineCanViewRMAs} />}
    />,
    // Global RMA routes
    <Route
      key={rmaListPath.pattern}
      path={rmaListPath.pattern}
      element={<RequireAuth component={InternalReturnGridView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaViewPath.pattern}
      path={rmaViewPath.pattern}
      element={<RequireAuth component={ReturnView} onEnter={determineIsStaff} />}
    />,
    /* for creating a new rma */
    <Route
      key={rmaCreatePath.pattern}
      path={rmaCreatePath.pattern}
      element={<RequireAuth component={NewReturnView} onEnter={determineIsStaff} />}
    />,
    /* for the already created rmas */
    <Route
      key={rmaIssuePath.pattern}
      path={rmaIssuePath.pattern}
      element={<RequireAuth component={EditReturnView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaQuickShipPath.pattern}
      path={rmaQuickShipPath.pattern}
      element={<RequireAuth component={QuickShipReturnView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaProcessPath.pattern}
      path={rmaProcessPath.pattern}
      element={<RequireAuth component={ProcessReturnView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaInvoicePath.pattern}
      path={rmaInvoicePath.pattern}
      element={<RequireAuth component={InvoiceReturnView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaResultSummaryPath.pattern}
      path={rmaResultSummaryPath.pattern}
      element={<RequireAuth component={ReturnPrintView} onEnter={determineIsStaff} />}
    />,
    <Route
      key={rmaBatchPrintPath.pattern}
      path={rmaBatchPrintPath.pattern}
      element={<RequireAuth component={ReturnPrintView} onEnter={determineIsStaff} />}
    />,
  ];
}
